import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  ViewportRuler,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, take, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { matDropDownAnimations } from '@nimbus-library/features/shared/components/material/mat-drop-down/mat-drop-down.animations';
import { convertFromTeamsClientCoordinates, isContainsInSVG } from '@nimbus-library/features/shared/components/material/utils';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { NimbusTooltipService } from '@nimbus-library/features/shared/services/tooltip/nimbusTooltip.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-parameters-overlay',
  templateUrl: './input-parameters-overlay.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [matDropDownAnimations.transformPanelWrap, matDropDownAnimations.transformPanel],
})
export class InputParametersOverlayComponent implements OnInit, OnDestroy {
  @ViewChild(CdkConnectedOverlay, { static: true })
  public overlayDir: CdkConnectedOverlay;
  @ViewChild('trigger', { static: true })
  public trigger: ElementRef;
  @ViewChild('panel')
  public panel: ElementRef;

  @Input()
  public insertOperationName: string;
  @Input()
  public customClass: string = '';
  @Input()
  public selectableBodyTemplateRef: TemplateRef<any>;
  @Input()
  public isLoading: boolean = false;
  @Input()
  public filtering: boolean = true;
  @Input()
  public isDisabled: boolean = false;
  @Input()
  public searchPredicate: (item: IIdName, searchValue: string) => boolean = undefined;
  @Input()
  public customSearchTemplate: TemplateRef<any>;

  @Output() public itemAdd: EventEmitter<IIdName> = new EventEmitter();
  @Output() public searchPhrase: EventEmitter<string> = new EventEmitter();
  @Output() public closePanel: EventEmitter<void> = new EventEmitter<void>();

  private _scrollTop = 0;
  private readonly destroy: Subject<void> = new Subject<void>();
  private triggerRect: ClientRect;
  private readonly inputDebounce = 500;

  public panelDoneAnimatingStream: Subject<string> = new Subject<string>();
  public offsetY: number = 0;
  public isScalable: boolean = false;
  public translations = TranslationKeys;
  public searchInput: UntypedFormControl = new UntypedFormControl();
  public icons = ICONS;
  public sourceSet$: BehaviorSubject<IIdName[]> = new BehaviorSubject([]);
  public filteredSet$: Observable<IIdName[]>;
  public isPanelOpen: boolean = false;
  public cdkConnectedPositions: ConnectedPosition[] = this.nimbusTooltipService.getCdkConnectedPositions();
  public wrapperClasses$: BehaviorSubject<{ [key: string]: boolean }> = new BehaviorSubject({
    [this.nimbusTooltipService.getCdkArrowCssClass()]: true,
  });
  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  @Input()
  public set optionsToAdd(val: IIdName[]) {
    this.sourceSet$.next(val);
  }

  public constructor(
    private elementReference: ElementRef<HTMLElement>,
    private _viewportRuler: ViewportRuler,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly nimbusTooltipService: NimbusTooltipService,
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
  ) {}

  public ngOnInit(): void {
    if (this.filtering) {
      this.filteredSet$ = combineLatest([this.searchInput.valueChanges.pipe(startWith(null)), this.sourceSet$]).pipe(
        takeUntil(this.destroy),
        debounceTime(this.inputDebounce),
        map(([searchValue, list]) => {
          if (!searchValue) {
            return list;
          }
          searchValue = searchValue.toLowerCase().trim();
          const searchPredicate: (item: IIdName, searchValue: string) => boolean = this.searchPredicate
            ? this.searchPredicate
            : (item: IIdName, searchValue: string) =>
                item.id.toLowerCase().includes(searchValue) || item.name.toLowerCase().includes(searchValue);
          return list.filter((item) => searchPredicate(item, searchValue));
        }),
      );
    } else {
      this.filteredSet$ = this.sourceSet$;
    }

    this.searchInput.valueChanges
      .pipe(debounceTime(this.inputDebounce))
      .subscribe((val) => this.searchPhrase.emit(!!val && val.length >= 3 ? val : null));

    this.panelDoneAnimatingStream.pipe(distinctUntilChanged(), takeUntil(this.destroy)).subscribe(() => {
      if (this.isPanelOpen) {
        this._scrollTop = 0;
      } else {
        this.overlayDir.offsetX = 0;
        this.changeDetectorRef.markForCheck();
      }
    });

    this._viewportRuler
      .change()
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        if (this.isPanelOpen) {
          this.triggerRect = this.trigger.nativeElement.getBoundingClientRect();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  public onPositionChange(e: ConnectedOverlayPositionChange): void {
    this.wrapperClasses$.next({
      [this.nimbusTooltipService.getCdkArrowCssClass(e)]: true,
      [this.customClass]: Boolean(this.customClass),
      customSearch: Boolean(this.customSearchTemplate),
      scalable: this.isScalable,
    });
  }

  public togglePanel(): void {
    this.isPanelOpen ? this.close() : this.open();
  }

  public open(): void {
    if (this.isDisabled || this.isPanelOpen) {
      return;
    }
    this.triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    this.searchInput.setValue(null);
    this.isPanelOpen = true;
    this._calculateOverlayPosition();
    this.changeDetectorRef.markForCheck();
  }

  public addItem(item: IIdName): void {
    const isDisabled = item['isDisabled'];
    if (!isDisabled) {
      this.itemAdd.emit(item);
    }
  }

  public close(): void {
    if (this.isPanelOpen) {
      this.isPanelOpen = false;
      this.closePanel.emit();
      this.changeDetectorRef.markForCheck();
      this.trigger.nativeElement.focus();
    }
  }

  public onAttached(): void {
    this.overlayDir.positionChange.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
      this._calculateOverlayOffsetX();
      this.panel.nativeElement.scrollTop = this._scrollTop;
      this.panel.nativeElement.querySelector('input')?.focus();
    });
  }

  private _calculateOverlayPosition(): void {
    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    const panelHeight = 300;
    const paddingTop = -30;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionY = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().y;
          const currentPositionY = convertFromTeamsClientCoordinates(currentScaledPositionY);
          this.offsetY =
            -1 * (currentScaledPositionY - currentPositionY - paddingTop * globalScale - (panelHeight * globalScale - panelHeight) / 2);
        } else {
          this.offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
        }
      } else {
        this.offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
      }
      this._scrollTop = 0;
      this._checkOverlayWithinViewport();
    });
  }

  private _checkOverlayWithinViewport(): void {
    const viewportSize = this._viewportRuler.getViewportSize();

    const topSpaceAvailable = this.triggerRect.top;
    const bottomSpaceAvailable = viewportSize.height - this.triggerRect.bottom;

    const panelHeightTop = Math.abs(this.offsetY);

    const totalPanelHeight = 300;
    const panelHeightBottom = totalPanelHeight - panelHeightTop - this.triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this._scrollTop = 0;
      this.offsetY = 0;
    } else if (panelHeightTop > topSpaceAvailable) {
      this._scrollTop = 0;
      this.offsetY = 0;
    }
  }

  private _calculateOverlayOffsetX(): void {
    const overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const viewportSize = this._viewportRuler.getViewportSize();

    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    const xPadding: number = 11 * globalScale;

    const fieldWidth = 230;

    const paddingWidth: number = xPadding * 2;
    let offsetX: number = xPadding;

    let leftOverflow: number = 0 - (overlayRect.left + offsetX);
    let rightOverflow: number = overlayRect.right + offsetX - viewportSize.width + paddingWidth;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        leftOverflow = 0 - (convertFromTeamsClientCoordinates(overlayRect.left) + offsetX);
        rightOverflow = convertFromTeamsClientCoordinates(overlayRect.right) + offsetX - viewportSize.width + paddingWidth;
      }
      if (leftOverflow > 0) {
        offsetX += leftOverflow;
      } else if (rightOverflow > 0) {
        offsetX -= rightOverflow;
      }
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionX = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().x;
          const currentPositionX = convertFromTeamsClientCoordinates(currentScaledPositionX);
          this.overlayDir.offsetX =
            -1 * (currentScaledPositionX - currentPositionX - offsetX - (fieldWidth * globalScale - fieldWidth) / 2);
        } else {
          this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
        }
      } else {
        this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
      }

      this.overlayDir.overlayRef.updatePosition();
    });
  }
}
